import { FlexController } from 'components/FlexControllerInput';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row
} from 'reactstrap';
import { FlexBootstrapTable } from '../../../../../../components/FlexBoostrapTable';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectMonitoringChat } from 'store/reduce/monitoringChat';
import { InternalTable } from './styled';
import MD5 from 'crypto-js/md5';
import { useWorker } from '../../../../../../hooks/useWork';
import logoG4 from '../../../../../../assets/img/theme/iconG4.png';
import { useUsersMyTeams } from 'hooks/useUserTeams';
/**
 * @typedef {Object} ChatInfo
 * @property {number} queueId
 * @property {number[]} customerTagIds
 * @property {string} channelId
 */

/**
 * @typedef {Object} AgentDataItem
 * @property {string} agentLogin
 * @property {string} agentName
 * @property {ChatInfo[]} closedChatsInfo
 * @property {ChatInfo[]} inAttendanceChatsInfo
 */

/**
 * @typedef {Object} QueueDataItem
 * @property {number} queueId
 * @property {string} queueName
 * @property {ChatInfo[]} closedChatsInfo
 * @property {ChatInfo[]} inAttendanceChatsInfo
 * @property {ChatInfo[]} inWaitChatsInfo
 */

/**
 * @typedef {Object} MonitoringData
 * @property {AgentDataItem[]} agentData
 * @property {QueueDataItem[]} queueData
 */

/**
 * @typedef {Object} MonitoringSyntheticProps
 * @property {AgentDataItem[]} agentData - Array of agent data items
 * @property {QueueDataItem[]} queueData - Array of queue data items
 * @property {Array<{value: number, label: string}>} contactTagOptions - Contact tag selection options
 * @property {Array<{value: number, label: string}>} queueOptions - Queue selection options
 * @property {Array<{value: string, label: string}>} agentOptions - Agent selection options
 */

/**
 * @param {MonitoringSyntheticProps} props
 */
const defaultColor = '#cbd5e1';
const redColor = '#f87171';
const orangeColor = '#fb923c';
const blueColor = '#22d3ee';
const greenColor = '#10b981';

const ProfileAgentCell = React.memo(({ src, alt }) => {
  const getDomain = useCallback(() => {
    let url =
      window.location.hostname === 'localhost'
        ? process.env.REACT_APP_PROXY_URL
        : window.location.origin;

    // Remove protocolo
    url = url.replace(/^https?:\/\//, '');

    // Remove a porta apenas para localhost ou se especificamente necessário
    // Caso contrário, mantenha a porta como parte do domínio
    if (window.location.hostname === 'localhost' && url.indexOf(':') !== -1) {
      url = url.substring(0, url.indexOf(':'));
    }

    if (url.indexOf('/') !== -1) {
      url = url.substring(0, url.indexOf('/'));
    }
    return url;
  }, []);

  const [imgSrc, setImgSrc] = useState(src);

  const handleError = () => {
    setImgSrc(logoG4);
  };

  const imageUrl = useMemo(() => {
    const domain = getDomain();
    const protocol = window.location.protocol;
    return `${protocol}//${domain}${
      src.includes('/configuration')
        ? src.substring(src.indexOf('/configuration'))
        : src
    }`;
  }, [getDomain, src]);

  useEffect(() => {
    setImgSrc(imageUrl);
  }, [imageUrl]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      style={{
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        boxShadow: '0 0 0 1px #00000010'
      }}
      onError={handleError}
    />
  );
});

ProfileAgentCell.displayName = 'ProfileAgentCell';

const agentColumns = [
  {
    dataField: 'agentName',
    text: 'Nome do agente',
    sort: true,
    width: 800,
    formatter: (_cell, row) => (
      <div
        title={row.agentName}
        style={{ display: 'flex', alignItems: 'left' }}
      >
        <ProfileAgentCell
          src={`/configuration/src/public/img/${MD5(row.agentLogin)}.png`}
          alt="profile"
        />
        <div
          className="ml-2 p-0"
          style={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <label
            className="m-0 p-0"
            style={{
              fontWeight: '500',
              fontSize: '1rem',
              textAlign: 'left'
            }}
          >
            {_cell}
          </label>
          <label
            className="m-0 p-0"
            style={{
              textAlign: 'left'
            }}
          >
            <small className="m-0 p-0">{row.agentLogin}</small>
          </label>
        </div>
      </div>
    )
  },
  {
    dataField: 'assigned',
    text: 'Atribuidas',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? blueColor : defaultColor}
        title={`Conversas atribuidas ao atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'inAttendance',
    text: 'Em atendimento',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? greenColor : defaultColor}
        title={`Conversas em atendimento do atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white ">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'agentCritical',
    text: 'Em atraso do agente',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? redColor : defaultColor}
        title={`Conversas com atraso de respostas critico do atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'customerCritical',
    text: 'Clientes inativos',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? orangeColor : defaultColor}
        title={`Conversas com clientes inativos do atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },

  {
    dataField: 'closed',
    text: 'Encerradas',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={'#444'}
        title={`Conversas encerradas do atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'closed',
    text: 'Total',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={'#444'}
        title={`Total de conversas com o atendente ${row.agentName} `}
      >
        <label className="m-0 p-0 text-white">
          {row.inAttendance + row.assigned}
        </label>
      </InternalTable>
    )
  }
];

const queueColumns = [
  {
    dataField: 'queueName',
    text: 'Nome da Fila',
    sort: true,
    formatter: (cell) => (
      <InternalTable>
        <label className="m-0 p-0 ">{cell}</label>{' '}
      </InternalTable>
    )
  },
  {
    dataField: 'inWait',
    text: 'Em Espera',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? blueColor : defaultColor}
        title={`Conversas em espera na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'inAttendance',
    text: 'Em Atendimento',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? greenColor : defaultColor}
        title={`Conversas em atendimento na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },

  {
    dataField: 'assigned',
    text: 'Atribuidas',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? blueColor : defaultColor}
        title={`Conversas atribuidas na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'agentCritical',
    text: 'Em atraso do agente',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? redColor : defaultColor}
        title={`Conversas com atraso de respostas criticas na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'customerCritical',
    text: 'Clientes inativos',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={_cell > 0 ? orangeColor : defaultColor}
        title={`Conversas com clientes inativos na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'closed',
    text: 'Encerrados',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={'#444'}
        title={`Conversas encerradas na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">{_cell}</label>
      </InternalTable>
    )
  },
  {
    dataField: 'total',
    text: 'Total',
    sort: true,
    formatter: (_cell, row) => (
      <InternalTable
        color={'#444'}
        title={`Total de conversas na fila ${row.queueName} `}
      >
        <label className="m-0 p-0 text-white">
          {row.inWait + row.assigned + row.inAttendance + row.closed}
        </label>
      </InternalTable>
    )
  }
];

const TableByAgent = React.memo(({ data }) => {
  return (
    <FlexBootstrapTable
      data={data}
      totalRecords={data.length}
      columns={agentColumns}
      showTotalRecords={false}
      headerTextAlign="center"
      cellTextAlign="center"
    />
  );
});

TableByAgent.displayName = 'TableByAgent';

const TableByQueue = React.memo(({ data }) => {
  return (
    <FlexBootstrapTable
      data={data}
      totalRecords={data.length}
      columns={queueColumns}
      showTotalRecords={false}
      headerTextAlign="center"
      cellTextAlign="center"
    />
  );
});

TableByQueue.displayName = 'TableByQueue';

const SelectVisionTypeDropDown = ({
  selectedVisionTab,
  setSelectedVisionTab
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle}>
      <DropdownToggle
        tag="span"
        className="selected-record-type-name ml-2"
        data-toggle="dropdown"
        aria-expanded={dropdownOpen}
      >
        {selectedVisionTab === 'agents' ? 'Atendentes' : 'Filas'}
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem header>Selecione</DropdownItem>

        <DropdownItem
          key={'queues'}
          onClick={() => setSelectedVisionTab('queues')}
        >
          Fila
        </DropdownItem>

        <DropdownItem
          key={'agents'}
          onClick={() => setSelectedVisionTab('agents')}
        >
          Atendente
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const MonitoringSynthetic = ({
  contactTagOptions = [],
  queueOptions = []
}) => {
  const [processedAgentData, setProcessedAgentData] = useState([]);
  const [processedQueueData, setProcessedQueueData] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const { data: monitoringSocketData } = useSelector(selectMonitoringChat);
  const { addMessageListener, sendMessageWork } = useWorker();
  const { control, setValue, errors, watch } = useForm();
  const [selectedVisionTab, setSelectedVisionTab] = useState('agents');
  const selectedTag = watch('contact_tag')?.map((t) => Number(t?.value)) || [];
  const selectedQueues = watch('queue')?.map((q) => q?.label) || [];
  const selectedAgent = watch('agent')?.map((a) => a?.value) || [];
  const hideEmptyRows = watch('hide_empty_rows');
  const { usersMyTeams } = useUsersMyTeams();

  const agentOptions = useMemo(() => {
    return allAgents
      .filter((agent) => usersMyTeams.includes(agent.agentLogin))
      .map((agent) => ({
        value: agent.agentLogin,
        label: agent.agentName
      }));
  }, [allAgents, usersMyTeams]);

  useEffect(() => {
    sendMessageWork({
      type: 'monitoring_synthetic',
      data: {
        data: monitoringSocketData,
        filters: {
          selectedTag,
          selectedQueues,
          selectedAgent,
          hideEmptyRows
        },
        usersMyTeams
      }
    });
  }, [
    monitoringSocketData,
    JSON.stringify(selectedTag),
    JSON.stringify(selectedQueues),
    JSON.stringify(selectedAgent),
    JSON.stringify(usersMyTeams)
    // imagesByLogin
  ]);

  useEffect(() => {
    addMessageListener((e) => {
      if (e.type === 'monitoring_synthetic') {
        setProcessedAgentData(e.data.agentData);
        setProcessedQueueData(e.data.queueData);
        if (allAgents.length === 0) {
          setAllAgents(e.data.agentData);
        }
      }
    });
  }, [allAgents.length]);

  const queues = useMemo(() => {
    return queueOptions.map((queue) => ({
      value: queue.id,
      label: queue.name
    }));
  }, [queueOptions]);

  return (
    <Container
      fluid
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '1rem',
        width: '100%',
        marginTop: '4rem',
        marginLeft: '0',
        marginRight: '0',
        padding: '0'
      }}
    >
      <Row>
        <Col md="3">
          <div className="small">
            <span className="font-weight-bold">
              <i className="far fa-eye"></i> Tipo de visão:
            </span>
            <SelectVisionTypeDropDown
              selectedVisionTab={selectedVisionTab}
              setSelectedVisionTab={setSelectedVisionTab}
            />
          </div>
        </Col>
      </Row>
      <Row>
        {selectedVisionTab !== 'queues' && (
          <Col md="3">
            <FlexController
              textoDoLabel="Atendente"
              type="selectable"
              name="agent"
              options={agentOptions}
              valueController={setValue}
              isMulti={true}
              closeMenuOnSelect={true}
              isClearable
              isSearchable
              disabled={false}
              portalTarget={true}
              errors={errors}
              control={control}
            />
          </Col>
        )}
        <Col md="3">
          <FlexController
            textoDoLabel="Fila"
            type="selectable"
            name="queue"
            options={queues}
            valueController={setValue}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            isSearchable
            disabled={false}
            portalTarget={true}
            errors={errors}
            control={control}
          />
        </Col>
        <Col md="3">
          <FlexController
            textoDoLabel="Tag contato"
            type="selectable"
            name="contact_tag"
            options={contactTagOptions}
            valueController={setValue}
            isMulti={true}
            closeMenuOnSelect={true}
            isClearable
            isSearchable
            disabled={false}
            portalTarget={true}
            errors={errors}
            control={control}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {selectedVisionTab === 'agents' ? (
            <TableByAgent data={processedAgentData} />
          ) : (
            <TableByQueue data={processedQueueData} />
          )}
        </Col>
      </Row>
    </Container>
  );
};
